// GrowthReportPage.js — Student Growth Report
// Matches client's reference design: CodeForReport.html

import React, { useEffect, useState } from "react";
import Layout from "../Layout";
import { Spin, message } from "antd";
import { Printer, BarChart3, FileText } from "lucide-react";
import { api } from "../authClient";
import "./GrowthReportPage.css";

// Domain display config — order matches client sample
const DOMAIN_ORDER = ["values", "academics", "skills", "competencies", "health", "career", "tech"];

const DOMAIN_META = {
    values:       { color: "purple", barClass: "purple",  sub: "Honesty • respect • responsibility • empathy • fairness • courage • digital behavior" },
    academics:    { color: "blue",   barClass: "blue",    sub: "Understanding • applying concepts • reasoning" },
    skills:       { color: "green",  barClass: "green",   sub: "Communication • teamwork • money sense • digital • idea thinking" },
    competencies: { color: "teal",   barClass: "teal",    sub: "Choices • ethics • consistency • reflection" },
    health:       { color: "orange", barClass: "orange",  sub: "Sleep • activity • food choices • screen habits • stress" },
    career:       { color: "pink",   barClass: "pink",    sub: "Interests • knowing roles • planning path" },
    tech:         { color: "teal",   barClass: "teal",    sub: "Digital + AI use • learning tools • safety" },
};

// Score → level number for CSS class
function scoreToLevelNum(score) {
    if (!score) return 1;
    if (score < 2.0) return 1;
    if (score < 3.0) return 2;
    if (score < 3.7) return 3;
    if (score < 4.4) return 4;
    return 5;
}

function getLevelLabel(domain) {
    if (!domain) return "—";
    if (domain.level && typeof domain.level === "object") return domain.level.label || "—";
    if (typeof domain.level === "string") return domain.level;
    return "—";
}

function getLevelNum(domain) {
    if (!domain) return 1;
    if (domain.level && typeof domain.level === "object") return domain.level.level || 1;
    return scoreToLevelNum(domain.score);
}

function getDomainLabel(key, domain) {
    const labels = {
        values: "Values",
        academics: "Academics",
        skills: "Skills (soft + hard + business)",
        competencies: "Maturity (decision + habits)",
        health: "Health (routine + wellbeing)",
        career: "Career awareness",
        tech: "Tech readiness",
    };
    return labels[key] || domain?.label || key;
}

function getTrendDot(trend) {
    if (!trend) return "stable";
    const dir = typeof trend === "object" ? trend.direction : trend;
    if (dir === "up" || dir === "improving") return "up";
    if (dir === "down" || dir === "declining") return "down";
    return "stable";
}

function getTrendLabel(trend) {
    if (!trend) return "Stable";
    if (typeof trend === "object") return trend.label || "Stable";
    return trend;
}

function formatTimestamp(ts) {
    if (!ts) return "—";
    try {
        const d = new Date(ts);
        const date = d.toISOString().split("T")[0];
        const time = d.toTimeString().slice(0, 5);
        return `${date} ${time}`;
    } catch { return "—"; }
}

function pct(score) {
    return score ? Math.round((score / 5) * 100) : 0;
}

// ─── Donut Component ───
function Donut({ pct: p, color, label }) {
    return (
        <div className="grn-donut-wrap">
            <div
                className="grn-donut"
                style={{ background: `conic-gradient(var(--${color}) 0 ${p}%, #E5E7EB ${p}% 100%)` }}
            >
                <div className="grn-donut-center">{p}%</div>
            </div>
            <div className="grn-legend">
                <div className="grn-leg"><span className="grn-swatch" style={{ background: `var(--${color})` }}></span> {label}</div>
                <div className="grn-leg"><span className="grn-swatch" style={{ background: "#E5E7EB" }}></span> Needs more practice</div>
            </div>
        </div>
    );
}

// ─── Callout Card ───
function Callout({ strip, title, badge, badgeColor, text, small }) {
    return (
        <div className={`grn-callout grn-strip-${strip}`}>
            <div className="grn-callout-head">
                <b>{title}</b>
                <span className={`grn-badge grn-b-${badgeColor}`}>{badge}</span>
            </div>
            <p>{text}</p>
            {small && <small>{small}</small>}
        </div>
    );
}

// ─── Mini Bar Row ───
function MiniBar({ name, pctVal, score, barColor }) {
    return (
        <div className="grn-row">
            <div className="grn-row-name">{name}</div>
            <div className="grn-bar"><span className={`grn-bar-fill grn-${barColor}`} style={{ width: `${pctVal}%` }}></span></div>
            <div className="grn-row-pct">{score}</div>
        </div>
    );
}

// ─── Confidence badge ───
function ConfBadge({ conf }) {
    const map = { high: "c-high", medium: "c-med", med: "c-med", low: "c-low" };
    const label = { high: "High", medium: "Med", med: "Med", low: "Low" };
    const cls = map[conf] || "c-med";
    return <span className={`grn-conf ${cls}`}><i></i>{label[conf] || conf || "Med"}</span>;
}


// ══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const GrowthReportPage = () => {
    const [activeTab, setActiveTab] = useState("latest");
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [reportHistory, setReportHistory] = useState([]);
    const reportRef = React.useRef(null);

    const handlePrint = () => {
        const content = reportRef.current;
        if (!content) return;
        // Collect all stylesheets from this page so the new window looks identical
        const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
            .map(el => el.outerHTML).join('\n');
        const win = window.open('', '_blank');
        win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Student Growth Report</title>
  ${styles}
  <style>
    body { margin: 0; padding: 12px 18px; background: #fff; font-family: 'Poppins', sans-serif; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    /* Compact spacing for print window */
    .grn-section { margin: 8px 0 !important; padding: 10px 14px !important; }
    .grn-card    { padding: 10px 12px !important; }
    .grn-kpis, .grn-charts, .grn-callouts, .grn-grid, .grn-roadmap { gap: 8px !important; }
    .grn-section-head { margin-bottom: 6px !important; padding-bottom: 6px !important; }
    /* Prevent large containers from causing blank pages */
    .grn-section-head { break-after: avoid; }
    .grn-kpi, .grn-callout, .grn-plan, .grn-scoring-box { break-inside: avoid; }
    details { break-inside: avoid; }
    details[open] summary ~ * { display: block !important; }
    .grn-evidence-table tr { break-inside: avoid; }
    .grn-topbar { break-inside: avoid; break-after: avoid; margin-bottom: 8px; }
    /* Shrink donut for compactness */
    .grn-donut { width: 80px !important; height: 80px !important; }
    .grn-donut-center { font-size: 13px !important; }
    @page { margin: 12mm 14mm; }
  </style>
</head>
<body>${content.outerHTML}</body>
</html>`);
        win.document.close();
        setTimeout(() => { win.print(); win.close(); }, 800);
    };

    useEffect(() => {
        if (activeTab === "latest") fetchLatestReport();
        if (activeTab === "history") fetchHistory();
    }, [activeTab]);

    const fetchLatestReport = async () => {
        setLoading(true);
        try {
            const res = await api.get("/student/growth-report");
            setReport(res.data);
        } catch (err) {
            if (err.response?.status !== 404) message.error("Failed to load report");
            setReport(null);
        }
        setLoading(false);
    };

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const res = await api.get("/student/growth-report/history");
            setReportHistory(res.data.reports || []);
        } catch { message.error("Failed to load report history"); }
        setLoading(false);
    };

    const fetchReportByPeriod = async (period) => {
        setLoading(true);
        try {
            const res = await api.get(`/student/growth-report/${encodeURIComponent(period)}`);
            setReport(res.data);
            setActiveTab("latest");
        } catch { message.error("Failed to load report"); }
        setLoading(false);
    };

    // ── Render the full report ──
    const renderReport = () => {
        if (loading) return <div className="grn-center-loader"><Spin size="large" /></div>;
        if (!report) {
            return (
                <div className="grn-empty">
                    <FileText size={48} color="#ccc" />
                    <h3>No Report Available Yet</h3>
                    <p>Ask your teacher to generate a growth report for you.</p>
                </div>
            );
        }

        const domains     = report.domains || {};
        const insights    = report.insights || {};
        const evidence    = report.evidence_entries || [];
        const plan        = insights.ninety_day_plan || {};
        const valSummary  = insights.values_summary || {};
        const domIns      = insights.domain_insights || {};
        const overallScore = report.overall_score || 0;
        const trendDir    = getTrendDot(report.trend);
        const trendLabel  = getTrendLabel(report.trend);
        const overallPct  = pct(overallScore);

        // Values domain data
        const valDomain   = domains.values || {};
        const valScore    = valDomain.score || 0;
        const valPct      = pct(valScore);
        const valSubMetrics = valDomain.sub_metrics || {};
        const valLevelNum = getLevelNum(valDomain);

        // Academic domain for donut
        const acadDomain  = domains.academics || {};
        const acadPct     = pct(acadDomain.score || 0);
        const acadConf    = acadDomain.confidence;
        const acadConfPct = acadConf === "high" ? 78 : acadConf === "medium" ? 55 : 35;

        return (
            <div className="grn-page" ref={reportRef}>

                {/* ── TOPBAR: Brand + Meta ── */}
                <div className="grn-topbar">
                    <div className="grn-brand">
                        <div className="grn-logo">HPC</div>
                        <div>
                            <h1 className="grn-brand-title">Student Growth Report</h1>
                            <div className="grn-brand-sub">Based on quizzes, teacher checks (rubrics), and reflections. Includes a full <b>Values</b> section.</div>
                        </div>
                    </div>

                    <div className="grn-meta">
                        <div className="grn-meta-top">
                            <div>
                                <div className="grn-label">Student</div>
                                <div className="grn-value grn-value-lg">{report.student_name || "—"}</div>
                                <div className="grn-label" style={{ marginTop: 8 }}>Grade / Section</div>
                                <div className="grn-value">{report.student_grade ? `${report.student_grade} • ${report.student_section || ""}` : "—"}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                                <div className="grn-label">Report Period</div>
                                <div className="grn-value">{report.report_period || "—"}</div>
                                <div className="grn-label" style={{ marginTop: 8 }}>Generated On</div>
                                <div className="grn-value">{report.generated_on ? new Date(report.generated_on).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</div>
                            </div>
                        </div>
                        <div className="grn-meta-grid">
                            <div>
                                <div className="grn-label">School</div>
                                <div className="grn-value">{report.school_name || "—"}</div>
                            </div>
                            <div>
                                <div className="grn-label">Student ID</div>
                                <div className="grn-value grn-mono">{report.student_roll_id || "—"}</div>
                            </div>
                            <div>
                                <div className="grn-label">Class Teacher</div>
                                <div className="grn-value">{report.class_teacher || "—"}</div>
                            </div>
                            <div>
                                <div className="grn-label">Data Used</div>
                                <div className="grn-value">Quiz • Rubric • Reflection</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── SECTION 1: Quick Summary ── */}
                <div className="grn-section">
                    <div className="grn-section-head">
                        <h2>1) Quick Summary</h2>
                        <div className="grn-pill">
                            <span>Overall:</span> <b>{overallScore} / 5</b>
                            <span className="grn-pill-sep">•</span>
                            <span>Trend:</span> <b>{trendLabel}</b>
                        </div>
                    </div>

                    <div className="grn-grid">
                        {/* Left: At a Glance */}
                        <div className="grn-card">
                            <h3 className="grn-card-title">At a Glance</h3>

                            {/* KPI Blocks */}
                            <div className="grn-kpis">
                                <div className="grn-kpi grn-kpi1">
                                    <div className="grn-kpi-t">Overall Score</div>
                                    <div className="grn-kpi-v">{overallScore}</div>
                                    <div className="grn-kpi-note">Across all areas</div>
                                    <div className="grn-trend">
                                        <span className={`grn-dot grn-${trendDir}`}></span> {trendLabel}
                                    </div>
                                </div>
                                <div className="grn-kpi grn-kpi2">
                                    <div className="grn-kpi-t">What the child does well</div>
                                    <div className="grn-kpi-v grn-kpi-v-sm">{insights.what_child_does_well || "—"}</div>
                                    <div className="grn-kpi-note">Strong in reasoning + practical thinking</div>
                                </div>
                                <div className="grn-kpi grn-kpi3">
                                    <div className="grn-kpi-t">What needs attention</div>
                                    <div className="grn-kpi-v grn-kpi-v-sm">{insights.what_needs_attention || "—"}</div>
                                    <div className="grn-kpi-note">Focus area for improvement</div>
                                </div>
                            </div>

                            {/* Charts */}
                            <div className="grn-charts">
                                {/* Academic Progress donut */}
                                <div className="grn-chart-box">
                                    <div className="grn-chart-title">
                                        <b>Academic Progress</b>
                                        <span>Strong answers vs practice needed</span>
                                    </div>
                                    <Donut pct={acadPct} color="blue" label="Strong / Good answers" />
                                    <div className="grn-bar-label">
                                        <span>Confidence in this data</span>
                                        <span className="grn-mono">{acadConfPct}%</span>
                                    </div>
                                    <div className="grn-bar"><span className={`grn-bar-fill grn-blue`} style={{ width: `${acadConfPct}%` }}></span></div>
                                    <div className="grn-label" style={{ marginTop: 8 }}>More quiz attempts + teacher notes = more accuracy.</div>
                                </div>

                                {/* Scores by Area mini-bars */}
                                <div className="grn-chart-box">
                                    <div className="grn-chart-title">
                                        <b>Scores by Area</b>
                                        <span>Quick comparison</span>
                                    </div>
                                    <div className="grn-mini-bars">
                                        {DOMAIN_ORDER.map(key => {
                                            const d = domains[key];
                                            const meta = DOMAIN_META[key] || {};
                                            const s = d?.score;
                                            const lvlNum = getLevelNum(d);
                                            return (
                                                <MiniBar
                                                    key={key}
                                                    name={getDomainLabel(key, d)}
                                                    pctVal={pct(s)}
                                                    score={`${lvlNum}/5`}
                                                    barColor={meta.barClass || "blue"}
                                                />
                                            );
                                        })}
                                    </div>
                                    <div className="grn-label" style={{ marginTop: 10 }}>
                                        Scale: 1 = starting • 2 = needs support • 3 = can do on own • 4 = strong • 5 = leads others
                                    </div>
                                </div>
                            </div>

                            {/* Callouts */}
                            <div className="grn-callouts">
                                <Callout
                                    strip="green" title="What we saw" badge="Good" badgeColor="green"
                                    text={domIns.values?.what_we_saw || "Child shows strong values and respects others."}
                                    small="Seen in values quiz + teacher observation notes."
                                />
                                <Callout
                                    strip="orange" title="What to work on" badge="Focus" badgeColor="orange"
                                    text={insights.what_needs_attention || "Some areas need more focused practice."}
                                    small="Seen in performance patterns across assessments."
                                />
                                <Callout
                                    strip="purple" title="Values overall" badge="Important" badgeColor="purple"
                                    text={valSummary.what_we_saw || "Values score shows consistent ethical choices."}
                                    small="Practice: peer pressure + honesty scenarios."
                                />
                                <Callout
                                    strip="blue" title="Quick win" badge="Easy" badgeColor="blue"
                                    text={insights.quick_win || "Use a 10-second check before submitting work to reduce silly mistakes."}
                                    small="Helps academics + responsibility."
                                />
                            </div>
                        </div>

                        {/* Right: Scores Table */}
                        <div className="grn-card">
                            <h3 className="grn-card-title">Scores (Simple Table)</h3>
                            <table className="grn-domain-table">
                                <thead>
                                    <tr>
                                        <th>Area</th>
                                        <th>Level</th>
                                        <th>Trend</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {DOMAIN_ORDER.map(key => {
                                        const d = domains[key];
                                        const lvlNum = getLevelNum(d);
                                        const lvlLabel = getLevelLabel(d);
                                        const td = getTrendDot(report.trend); // per-domain trend could be added later
                                        const tl = getTrendLabel(report.trend);
                                        return (
                                            <tr key={key}>
                                                <td><b>{getDomainLabel(key, d)}</b></td>
                                                <td><span className={`grn-lvl grn-lvl${lvlNum}`}>{lvlNum} / 5</span></td>
                                                <td>
                                                    <span className="grn-pill grn-pill-sm">
                                                        <span className={`grn-dot grn-${td}`}></span>
                                                        <b>{tl}</b>
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <div className="grn-label" style={{ marginTop: 10 }}>
                                Note: Each level is decided using repeated quiz performance + teacher rubric + reflection (proof below).
                            </div>

                            {/* Scoring formula box */}
                            <div className="grn-scoring-box">
                                <div className="grn-scoring-title">How Scoring Works</div>
                                <div className="grn-scoring-row"><span>Quiz score</span><b>40%</b></div>
                                <div className="grn-scoring-row"><span>Teacher rubric</span><b>40%</b></div>
                                <div className="grn-scoring-row"><span>Student reflection</span><b>20%</b></div>
                                <div className="grn-scoring-levels">
                                    <span>0–1.9 = L1</span><span>2.0–2.9 = L2</span><span>3.0–3.6 = L3</span>
                                    <span>3.7–4.3 = L4</span><span>4.4–5.0 = L5</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── SECTION 2: Values (Most Important) ── */}
                <div className="grn-section">
                    <div className="grn-section-head">
                        <h2>2) Values (Most Important)</h2>
                        <div className="grn-pill">
                            <span>Values Level:</span> <b>{valScore.toFixed ? valScore.toFixed(1) : valScore} / 5</b>
                            <span className="grn-pill-sep">•</span>
                            <span>Data:</span> <b>{valDomain.confidence === "high" ? "Good" : valDomain.confidence === "medium" ? "Medium" : "Limited"}</b>
                        </div>
                    </div>

                    <div className="grn-grid">
                        {/* Left: Values Chart */}
                        <div className="grn-card">
                            <h3 className="grn-card-title">Values Chart</h3>

                            <div className="grn-chart-box" style={{ marginTop: 0 }}>
                                <div className="grn-chart-title">
                                    <b>Values Score</b>
                                    <span>Overall values performance</span>
                                </div>
                                <Donut pct={valPct} color="purple" label="Good value-based choices" />
                                <div className="grn-bar-label">
                                    <span>Confidence in values score</span>
                                    <span className="grn-mono">{Math.min(valPct + 5, 100)}%</span>
                                </div>
                                <div className="grn-bar"><span className="grn-bar-fill grn-purple" style={{ width: `${Math.min(valPct + 5, 100)}%` }}></span></div>
                                <div className="grn-label" style={{ marginTop: 8 }}>Confidence improves with more "real-life" situations.</div>
                            </div>

                            {/* Values sub-metrics breakdown */}
                            <div className="grn-chart-box" style={{ marginTop: 12 }}>
                                <div className="grn-chart-title">
                                    <b>Values Breakdown</b>
                                    <span>Small scores inside values</span>
                                </div>
                                <div className="grn-mini-bars">
                                    {Object.keys(valSubMetrics).length > 0
                                        ? Object.entries(valSubMetrics).map(([k, v]) => {
                                            const sc = v.score || 0;
                                            const colors = ["purple", "blue", "teal", "pink", "green", "orange", "red", "teal"];
                                            const idx = Object.keys(valSubMetrics).indexOf(k);
                                            return (
                                                <MiniBar
                                                    key={k}
                                                    name={k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                                                    pctVal={pct(sc)}
                                                    score={sc.toFixed ? sc.toFixed(1) : sc}
                                                    barColor={colors[idx % colors.length]}
                                                />
                                            );
                                        })
                                        : (
                                            // Placeholder sub-metrics when no data
                                            ["Honesty", "Respect", "Responsibility", "Empathy", "Fairness", "Self-control", "Courage", "Digital behavior"].map((name, i) => (
                                                <MiniBar key={name} name={name} pctVal={valPct} score="—" barColor={["purple","blue","teal","pink","green","orange","red","teal"][i]} />
                                            ))
                                        )
                                    }
                                </div>
                                <div className="grn-label" style={{ marginTop: 10 }}>
                                    Tip: Use scenario questions + "why" answers to score these properly.
                                </div>
                            </div>
                        </div>

                        {/* Right: Values Summary */}
                        <div className="grn-card">
                            <h3 className="grn-card-title">Values Summary (Easy)</h3>
                            <div className="grn-callouts" style={{ marginTop: 0 }}>
                                <Callout
                                    strip="green" title="What we saw" badge="Good" badgeColor="green"
                                    text={valSummary.what_we_saw || "Child usually chooses the right thing and respects others in team settings."}
                                    small="Seen in classroom + teamwork tasks."
                                />
                                <Callout
                                    strip="orange" title="Where it gets hard" badge="Pressure" badgeColor="orange"
                                    text={valSummary.where_it_gets_hard || "In peer pressure cases, decisions can become shaky."}
                                    small="Seen in scenario quiz patterns."
                                />
                                <Callout
                                    strip="purple" title="Simple habit" badge="1 rule" badgeColor="purple"
                                    text={valSummary.simple_habit || 'Before acting: "Will I feel proud of this tomorrow?" If not, stop.'}
                                    small="Builds honesty + courage + self-control."
                                />
                                <Callout
                                    strip="teal" title="One action for next month" badge="Practice" badgeColor="teal"
                                    text={valSummary.one_action || 'Do 1 values case per week + write a 3-line reflection ("What I chose + why").'}
                                    small="Improves maturity and consistency."
                                />
                            </div>
                            <div className="grn-label" style={{ marginTop: 10 }}>
                                Values level meaning: 1 = starting • 2 = needs support • 3 = mostly right • 4 = strong • 5 = role model
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── SECTION 3: Details (Accordion) ── */}
                <div className="grn-section">
                    <div className="grn-section-head">
                        <h2>3) Details (Easy to Read)</h2>
                        <div className="grn-pill"><span>Style:</span> <b>Simple + Actionable</b></div>
                    </div>

                    <div className="grn-accordion">
                        {DOMAIN_ORDER.map((key, i) => {
                            const d = domains[key] || {};
                            const ins = domIns[key] || {};
                            const lvlNum = getLevelNum(d);
                            const meta = DOMAIN_META[key] || {};
                            const conf = d.confidence || "medium";
                            const confLabel = conf === "high" ? "High data" : conf === "medium" ? "Medium data" : "Low data";
                            const confCls = conf === "high" ? "c-high" : conf === "low" ? "c-low" : "c-med";
                            return (
                                <details key={key} open={i === 0}>
                                    <summary>
                                        <div className="grn-sum-left">
                                            <div className="grn-sum-title">{getDomainLabel(key, d)}</div>
                                            <div className="grn-sum-sub">{meta.sub}</div>
                                        </div>
                                        <div className="grn-sum-right">
                                            <span className={`grn-lvl grn-lvl${lvlNum}`}>{lvlNum} / 5</span>
                                            <span className={`grn-conf ${confCls}`}><i></i>{confLabel}</span>
                                        </div>
                                    </summary>
                                    <div className="grn-accordion-content">
                                        <div className="grn-callouts" style={{ marginTop: 0 }}>
                                            <Callout
                                                strip={ins.what_we_saw_label === "Good" ? "green" : ins.what_we_saw_label === "Growing" ? "teal" : "orange"}
                                                title="What we saw"
                                                badge={ins.what_we_saw_label || "Good"}
                                                badgeColor={ins.what_we_saw_label === "Good" ? "green" : ins.what_we_saw_label === "Growing" ? "teal" : "orange"}
                                                text={ins.what_we_saw || "Performance data collected across assessments."}
                                                small="Based on quiz + teacher observation + reflection."
                                            />
                                            <Callout
                                                strip="orange" title="What to improve" badge="Focus" badgeColor="orange"
                                                text={ins.what_to_improve || "Continue building skills in this area."}
                                                small={ins.simple_habit ? `Habit: ${ins.simple_habit}` : "Practice regularly."}
                                            />
                                        </div>
                                    </div>
                                </details>
                            );
                        })}
                    </div>
                </div>

                {/* ── SECTION 4: Proof ── */}
                <div className="grn-section">
                    <div className="grn-section-head">
                        <h2>4) Proof (How we decided the levels)</h2>
                        <div className="grn-pill"><span>Proof:</span> <b>Evidence IDs + rubric scores</b></div>
                    </div>

                    <div className="grn-evidence-wrap">
                        <div className="grn-card" style={{ marginBottom: 12 }}>
                            <h3 className="grn-card-title">Simple Explanation</h3>
                            <div className="grn-label">What we used</div>
                            <div className="grn-value" style={{ marginTop: 6 }}>
                                We used quiz questions + teacher rubric + short reflections. Every score below is linked to an evidence ID.
                            </div>
                            <div className="grn-label" style={{ marginTop: 10 }}>Why this is fair</div>
                            <div className="grn-value" style={{ marginTop: 6 }}>
                                Not based on one test. It's based on repeated performance in different situations.
                            </div>
                        </div>

                        {evidence.length > 0 ? (
                            <table className="grn-evidence-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 90 }}>Area</th>
                                        <th style={{ width: 140 }}>What we checked</th>
                                        <th style={{ width: 120 }}>Evidence ID</th>
                                        <th>What happened</th>
                                        <th style={{ width: 75 }}>Score</th>
                                        <th style={{ width: 90 }}>Data</th>
                                        <th style={{ width: 140 }}>Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {evidence.slice(0, 15).map((ev, i) => {
                                        const what = ev.observation_text || ev.question || "—";
                                        const sc = ev.score !== null && ev.score !== undefined ? Number(ev.score).toFixed(1) : "—";
                                        const domainKey = ev.domain || "";
                                        const isValues = domainKey === "values";
                                        return (
                                            <tr key={i}>
                                                <td><b style={{ fontWeight: isValues ? 700 : 500 }}>{getDomainLabel(domainKey, {})}</b></td>
                                                <td>{(ev.sub_metric || "").replace(/_/g, " ")}</td>
                                                <td className="grn-mono" style={{ fontSize: 11 }}>{ev.evidence_id || "—"}</td>
                                                <td style={{ fontSize: 12 }}>{what.length > 120 ? what.slice(0, 120) + "…" : what}</td>
                                                <td><span className="grn-scorebox">{sc}</span></td>
                                                <td><ConfBadge conf={ev.confidence || "medium"} /></td>
                                                <td className="grn-mono" style={{ fontSize: 11 }}>{formatTimestamp(ev.timestamp)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="grn-label" style={{ padding: 16, textAlign: "center" }}>
                                No evidence data yet. Evidence will appear after quizzes, teacher observations, and reflections are recorded.
                            </div>
                        )}

                        <div className="grn-label" style={{ marginTop: 10 }}>
                            Evidence IDs are linked to your quiz/observation system. This section is your "proof".
                        </div>
                    </div>
                </div>

                {/* ── SECTION 5: 90 Day Plan ── */}
                <div className="grn-section">
                    <div className="grn-section-head">
                        <h2>5) Next 90 Days Plan</h2>
                        <div className="grn-pill"><span>Goal:</span> <b>Small steps → big improvement</b></div>
                    </div>

                    <div className="grn-roadmap">
                        <div className="grn-plan grn-plan1">
                            <h4>Values (keep strong)</h4>
                            <ul>
                                {(plan.keep_strong || [
                                    "1 values case per week (peer pressure / honesty / fairness).",
                                    "3-line reflection after each: What I chose + why + what I'll do next time.",
                                    "Do one \"kind action\" per week (small, real).",
                                ]).map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                        </div>
                        <div className="grn-plan grn-plan2">
                            <h4>Improve fast (focus areas)</h4>
                            <ul>
                                {(plan.improve_fast || [
                                    "10-second check before submit to reduce small mistakes.",
                                    "Plan → Do → Check for homework and projects.",
                                    "Writing: Point → Reason → Example for clear answers.",
                                ]).map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                        </div>
                        <div className="grn-plan grn-plan3">
                            <h4>Future-ready</h4>
                            <ul>
                                {(plan.future_ready || [
                                    "Career: 2 roles/month + one small activity.",
                                    "Tech safety: 10-min quiz weekly (privacy/scams/footprint).",
                                    "Health: 7-day tracker (sleep + 20-min movement + water).",
                                ]).map((item, i) => <li key={i}>{item}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="grn-foot">
                    Simple + colorful report • Print-friendly • Evidence-based scoring
                </div>
            </div>
        );
    };

    // ── History Tab ──
    const renderHistory = () => {
        if (loading) return <div className="grn-center-loader"><Spin size="large" /></div>;

        const scores = reportHistory.map(r => r.overall_score).filter(Boolean);
        const bestScore = scores.length ? Math.max(...scores).toFixed(1) : "—";
        const latestScore = scores.length ? scores[0].toFixed(1) : "—";

        return (
            <div className="font-[poppins]">
                {/* ── Big Header ── */}
                <div className="pt-6 pb-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-5" style={{ backgroundColor: "#D67C2C" }}>
                        <BarChart3 className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-4xl font-bold text-[#D67C2C] mb-3">Growth Report History</h1>
                    <p className="text-[#1D3469] text-base font-medium max-w-2xl mx-auto">
                        View all past growth reports generated by your teacher. Click any row to open the full report.
                    </p>
                </div>

                {/* ── Stats Cards ── */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                    {[
                        { label: "Total Reports", value: reportHistory.length, icon: <FileText className="w-6 h-6 text-blue-600" />, bg: "bg-blue-100", color: "text-blue-600" },
                        { label: "Latest Score", value: `${latestScore} / 5`, icon: <BarChart3 className="w-6 h-6 text-[#D67C2C]" />, bg: "bg-orange-100", color: "text-[#D67C2C]" },
                        { label: "Best Score", value: `${bestScore} / 5`, icon: <Printer className="w-6 h-6 text-green-600" />, bg: "bg-green-100", color: "text-green-600" },
                    ].map(s => (
                        <div key={s.label} className="bg-white rounded-xl shadow-md border border-gray-200 hover:shadow-lg transition-all duration-300">
                            <div className="p-6 flex items-center gap-4">
                                <div className={`p-3 rounded-full ${s.bg}`}>{s.icon}</div>
                                <div>
                                    <p className="text-sm font-medium text-gray-500">{s.label}</p>
                                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Table ── */}
                <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                    {/* Table header bar */}
                    <div className="p-5 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                            <FileText className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">All Reports</h2>
                        <span className="ml-auto text-sm text-gray-400">{reportHistory.length} report{reportHistory.length !== 1 ? "s" : ""}</span>
                    </div>

                    {reportHistory.length === 0 ? (
                        <div className="text-center py-20">
                            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-500 mb-1">No Past Reports</h3>
                            <p className="text-sm text-gray-400">Reports will appear here as they are generated each term.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {["#", "Period", "Generated On", "Overall Score", "Trend", "Evidence", "Domains", ""].map(h => (
                                            <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {reportHistory.map((r, i) => {
                                        const sc = r.overall_score;
                                        const lvl = sc >= 4.4 ? 5 : sc >= 3.7 ? 4 : sc >= 3.0 ? 3 : sc >= 2.0 ? 2 : 1;
                                        const trendDir = getTrendDot(r.trend);
                                        const trendLbl = getTrendLabel(r.trend);
                                        const domainCount = Object.keys(r.domains || {}).length;
                                        const trendColor = trendDir === "up" ? "text-green-600" : trendDir === "down" ? "text-red-500" : "text-gray-500";

                                        return (
                                            <tr
                                                key={r._id || i}
                                                className="hover:bg-orange-50 transition-colors cursor-pointer"
                                                onClick={() => fetchReportByPeriod(r.report_period)}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-orange-50 border border-orange-200 text-xs font-bold text-[#D67C2C]">
                                                        {reportHistory.length - i}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="font-semibold text-gray-900 text-sm">{r.report_period || "—"}</div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                                    {r.generated_on ? new Date(r.generated_on).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full text-white font-bold text-sm grn-score-lvl${lvl}`}>
                                                        {sc ? sc.toFixed(1) : "—"}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`flex items-center gap-1.5 text-sm font-medium ${trendColor}`}>
                                                        <span className={`grn-dot grn-${trendDir}`}></span>
                                                        {trendLbl}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                                    {r.total_evidence ? `${r.total_evidence} pts` : "—"}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                                    {domainCount > 0 ? `${domainCount} areas` : "—"}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                                    <button className="px-3 py-1.5 text-xs font-semibold text-[#D67C2C] border border-[#D67C2C] rounded-lg hover:bg-[#D67C2C] hover:text-white transition-colors">
                                                        View Report →
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Layout>
            <div className="grn-container">
                {/* ── Page header bar ── */}
                <div className="grn-page-header no-print">
                    <div className="grn-page-header-left">
                        <div className="grn-page-title-block">
                            <BarChart3 size={20} className="grn-page-title-icon" />
                            <h1 className="grn-page-title">Growth Report</h1>
                        </div>
                        <div className="grn-tabs">
                            <button className={`grn-tab ${activeTab === "latest" ? "active" : ""}`} onClick={() => setActiveTab("latest")}>
                                <FileText size={13} /> Latest Report
                            </button>
                            <button className={`grn-tab ${activeTab === "history" ? "active" : ""}`} onClick={() => setActiveTab("history")}>
                                <BarChart3 size={13} /> History
                            </button>
                        </div>
                    </div>
                    {activeTab === "latest" && report && (
                        <button className="grn-print-btn" onClick={handlePrint}>
                            <Printer size={14} /> Print Report
                        </button>
                    )}
                </div>

                {activeTab === "latest" && renderReport()}
                {activeTab === "history" && (
                    <div style={{ padding: "0 32px 60px" }}>
                        {renderHistory()}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default GrowthReportPage;
